import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Stack,
  Box,
  Tooltip,
  useTheme,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useSnackbar } from "notistack";

interface CustomerDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  customerPhone?: string | null;
  customerEmail?: string | null;
  customerAddress?: string | null;
}

const customerDetailDialog = ({
  open,
  onClose,
  customerEmail,
  customerPhone,
  customerAddress,
}: CustomerDetailsDialogProps) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { enqueueSnackbar } = useSnackbar();
  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      PaperProps={{
        sx: {
          borderRadius: 4, // 4 * 8px = 32px or use '16px' if you prefer
        },
      }}
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle
        sx={(theme) => ({
          backgroundColor:
            theme.palette.mode === "light"
              ? theme.palette.primary.main
              : "#181F2A",
          color:
            theme.palette.mode === "light"
              ? theme.palette.primary.contrastText
              : "#fff",
        })}
      >
        {/* <Typography>New Segment</Typography> */}
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography
            pl="15px"
            sx={{
              fontStyle: "normal",
              fontWeight: 400,
              fontSize: "16px",
            }}
          >
            Customer Details
          </Typography>

          <IconButton sx={{ color: "white" }} onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} mt={1}>
          {customerPhone && 
           customerPhone.trim().length > 0 && 
           !customerPhone.trim().match(/^\(\s*\)\s*-\s*x$/) && (
            <Stack direction="row" spacing={1} alignItems="center">
              <PhoneIcon />
              <Typography variant="body1" sx={{ textDecoration: "underline" }}>
                {customerPhone}
              </Typography>
              <Tooltip title="Copy to Clipboard">
                <IconButton
                  sx={(theme) => ({
                    color: theme.palette.text.secondary,
                    cursor: "pointer",
                    transition: "color 0.3s ease",
                    "&:hover": {
                      color: theme.palette.primary.main,
                    },
                  })}
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(customerPhone);
                    enqueueSnackbar("Copied to clipboard", {
                      variant: "success",
                    });
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}

          {customerEmail && customerEmail.trim().length > 0 && (
            <Stack direction="row" spacing={1} alignItems="center">
              <MailOutlineIcon />
              <Typography variant="body1" sx={{ textDecoration: "underline" }}>
                {customerEmail}
              </Typography>
              <Tooltip title="Copy to Clipboard">
                <IconButton
                  sx={(theme) => ({
                    color: theme.palette.text.secondary,
                    cursor: "pointer",
                    transition: "color 0.3s ease",
                    "&:hover": {
                      color: theme.palette.primary.main,
                    },
                  })}
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(customerEmail);
                    enqueueSnackbar("Copied to clipboard", {
                      variant: "success",
                    });
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}

          {customerAddress && customerAddress.trim().length > 0 && (
            <Stack direction="row" spacing={1} alignItems="center">
              <LocationOnIcon />
              <Typography variant="body1" sx={{ textDecoration: "underline" }}>
                {customerAddress}
              </Typography>
              <Tooltip title="Copy to Clipboard">
                <IconButton
                  size="small"
                  sx={(theme) => ({
                    color: theme.palette.text.secondary,
                    cursor: "pointer",
                    transition: "color 0.3s ease",
                    "&:hover": {
                      color: theme.palette.primary.main,
                    },
                  })}
                  onClick={() => {
                    navigator.clipboard.writeText(customerAddress);
                    enqueueSnackbar("Copied to clipboard", {
                      variant: "success",
                    });
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default customerDetailDialog;
